/**
 * Grind mode storage facade.
 * Uses normalized IndexedDB tables (no blobs). Maintains in-memory cache for sync reads.
 *
 * Tables:
 * - settings: global scalar key-value
 * - user_prefs: per-user scalar key-value
 * - play_list: one row per tournament (bounded row size)
 * - list_members: one row per (userId, listType, scheduleId)
 */

import { IndexedDBBackend } from "./idb-backend";
import {
  GrindModeSettingsPatch,
  GrindModeUserListName,
} from "./interfaces";
import { migrateFromLocalStorage } from "./migrate-localstorage";
import {
  SETTING_KEYS,
  type ListMemberType,
  type PlayListRow,
} from "./schema";

type UserCache = {
  prefs: Record<string, string>;
  playList: PlayListRow[];
  listIds: Record<ListMemberType, number[]>;
};

export class GrindModeLocalStorage {
  private static backend = new IndexedDBBackend();
  private static initialized = false;
  private static initPromise: Promise<void> | null = null;
  private static settingsCache = new Map<string, string>();
  private static userCache = new Map<number, UserCache>();
  private static lastLoadedUserId: number | null = null;

  private static readonly listNameToType: Record<GrindModeUserListName, ListMemberType | "play"> = {
    play: "play",
    skip: "skip",
    bustLate: "bustLate",
    hidden: "hidden",
    removedBustLate: "removedBustLate",
  };

  static async initialize(): Promise<void>;
  static async initialize(userId: number | string | undefined): Promise<void>;
  static async initialize(userId?: number | string): Promise<void> {
    if (this.initialized && userId == null) return;
    const uid = userId != null ? Number(userId) : null;
    if (this.initialized && uid === this.lastLoadedUserId) return;

    if (!this.initPromise) {
      this.initPromise = this.bootstrap(uid);
    } else if (uid !== this.lastLoadedUserId) {
      await this.initPromise;
      this.initPromise = this.loadUserData(uid);
    }

    await this.initPromise;
    if (uid != null) this.lastLoadedUserId = uid;
  }

  private static async bootstrap(userId: number | null): Promise<void> {
    try {
      await migrateFromLocalStorage(this.backend);
      const settings = await this.backend.getAllSettings();
      this.settingsCache.clear();
      for (const [k, v] of Object.entries(settings)) {
        this.settingsCache.set(k, v);
      }
      this.initialized = true;
      if (userId != null) await this.loadUserData(userId);
    } finally {
      this.initPromise = null;
    }
  }

  private static async loadUserData(userId: number | null): Promise<void> {
    if (userId == null) return;
    const [prefs, playList, ...listArrays] = await Promise.all([
      this.backend.getAllUserPrefs(userId),
      this.backend.getPlayList(userId),
      this.backend.getListIds(userId, "skip"),
      this.backend.getListIds(userId, "bustLate"),
      this.backend.getListIds(userId, "hidden"),
      this.backend.getListIds(userId, "removedBustLate"),
    ]);
    this.userCache.set(userId, {
      prefs,
      playList,
      listIds: {
        skip: listArrays[0],
        bustLate: listArrays[1],
        hidden: listArrays[2],
        removedBustLate: listArrays[3],
      },
    });
    this.initPromise = null;
  }

  private static ensureUserCache(userId: number | string): UserCache | null {
    const uid = Number(userId);
    return this.userCache.get(uid) ?? null;
  }

  static async ready(): Promise<void> {
    await this.initialize();
  }

  /** @deprecated Backend is fixed to IndexedDB. Kept for interface compatibility. */
  static setBackend(_backend: unknown): void {
    /* no-op: normalized table backend is fixed */
  }

  static getItem(key: string): string | null {
    const settingKey = LEGACY_TO_SETTING[key];
    if (settingKey) return this.settingsCache.get(settingKey) ?? null;
    return this.settingsCache.get(key) ?? null;
  }

  static setItem(key: string, value: string): void {
    const settingKey = LEGACY_TO_SETTING[key] ?? key;
    this.settingsCache.set(settingKey, value);
    this.backend.setSetting(settingKey, value).catch(() => {});
  }

  static removeItem(key: string): void {
    const settingKey = LEGACY_TO_SETTING[key] ?? key;
    this.settingsCache.delete(settingKey);
    this.backend.removeSetting(settingKey).catch(() => {});
  }

  static clear(): void {
    this.settingsCache.clear();
    this.userCache.clear();
    this.lastLoadedUserId = null;
    this.backend.clear().catch(() => {});
  }

  static getJSON<T>(key: string, fallback: T): T {
    const v = this.getItem(key);
    if (!v) return fallback;
    try {
      return JSON.parse(v) as T;
    } catch {
      return fallback;
    }
  }

  static setJSON(key: string, value: unknown): void {
    this.setItem(key, JSON.stringify(value));
  }

  static async getItemAsync(key: string): Promise<string | null> {
    await this.initialize();
    return this.getItem(key);
  }

  static async setItemAsync(key: string, value: string): Promise<void> {
    await this.initialize();
    this.settingsCache.set(LEGACY_TO_SETTING[key] ?? key, value);
    await this.backend.setSetting(LEGACY_TO_SETTING[key] ?? key, value);
  }

  static async removeItemAsync(key: string): Promise<void> {
    await this.initialize();
    const k = LEGACY_TO_SETTING[key] ?? key;
    this.settingsCache.delete(k);
    await this.backend.removeSetting(k);
  }

  static async clearAsync(): Promise<void> {
    await this.initialize();
    this.clear();
  }

  static async getJSONAsync<T>(key: string, fallback: T): Promise<T> {
    const v = await this.getItemAsync(key);
    if (!v) return fallback;
    try {
      return JSON.parse(v) as T;
    } catch {
      return fallback;
    }
  }

  static async setJSONAsync(key: string, value: unknown): Promise<void> {
    await this.setItemAsync(key, JSON.stringify(value));
  }

  static getUserList<T>(userId: number | string, listName: GrindModeUserListName, fallback: T): T {
    this.initialize(userId); // fire-and-forget to ensure user cache loads
    const cache = this.ensureUserCache(userId);
    const uid = Number(userId);
    const type = this.listNameToType[listName];
    if (type === "play") {
      if (!cache) return fallback;
      const items = cache.playList.map((r) => r.data);
      return (items.length ? items : fallback) as T;
    }
    if (!cache) return fallback;
    const ids = cache.listIds[type];
    return (ids?.length ? ids : fallback) as T;
  }

  static setUserList(userId: number | string, listName: GrindModeUserListName, value: unknown): void {
    const uid = Number(userId);
    const type = this.listNameToType[listName];
    let cache = this.ensureUserCache(userId);
    if (!cache) {
      cache = { prefs: {}, playList: [], listIds: { skip: [], bustLate: [], hidden: [], removedBustLate: [] } };
      this.userCache.set(uid, cache);
    }
    if (type === "play") {
      const items = Array.isArray(value) ? value as Array<Record<string, unknown>> : [];
      cache.playList = items.map((data, i) => ({
        userId: uid,
        scheduleId: (data.tournament_id ?? data.id) as number,
        data,
        addedAt: Date.now() + i,
      }));
      this.backend.setPlayList(uid, items).catch(() => {});
    } else {
      const ids = Array.isArray(value) ? (value as number[]).map((x) => Number(x)).filter((n) => !isNaN(n)) : [];
      cache.listIds[type] = ids;
      this.backend.setListIds(uid, type, ids).catch(() => {});
    }
  }

  static clearUserList(userId: number | string, listName: GrindModeUserListName): void {
    this.setUserList(userId, listName, listName === "play" ? [] : []);
  }

  static updateSettings(settings: GrindModeSettingsPatch): void {
    const keys = SETTING_KEYS;
    if (settings.tableZoom !== undefined) this.setItem(keys.tableZoom, settings.tableZoom.toFixed(1));
    if (settings.autoResizeTable !== undefined) this.setItem(keys.autoResizeTable, String(settings.autoResizeTable));
    if (settings.timeMaxLate !== undefined) {
      if (settings.timeMaxLate === null || settings.timeMaxLate === "") this.removeItem(keys.timeMaxLate);
      else this.setItem(keys.timeMaxLate, settings.timeMaxLate);
    }
    if (settings.autoAddMaxLate !== undefined) {
      if (settings.autoAddMaxLate) this.setItem(keys.autoAddMaxLate, "true");
      else this.removeItem(keys.autoAddMaxLate);
    }
    const refresh = settings.refreshSchedulesRequested ?? settings.refreshSchedulesFlag;
    if (refresh !== undefined) {
      if (refresh) this.setItem(keys.refreshSchedules, "true");
      else this.removeItem(keys.refreshSchedules);
    }
    if (settings.tourColumnsSaved !== undefined) this.setJSON(keys.tourColumnsSaved, settings.tourColumnsSaved);
    if (settings.hourGroupColumn !== undefined) this.setItem(keys.hourGroupColumn, settings.hourGroupColumn);
    if (settings.gridColumnState !== undefined) this.setJSON(keys.gridColumnState, settings.gridColumnState);
    if (settings.clearGridColumnState) this.removeItem(keys.gridColumnState);
    if (settings.currencyRatesCache !== undefined) this.setJSON(keys.currencyRatesCache, settings.currencyRatesCache);
  }

  static getTableZoom(fallback = 1): number {
    const v = Number(this.getItem(SETTING_KEYS.tableZoom));
    return Number.isFinite(v) && v > 0 ? v : fallback;
  }

  static getAutoResizeTable(fallback = false): boolean {
    return this.getJSON(SETTING_KEYS.autoResizeTable, fallback);
  }

  static getTimeMaxLate(): string | null {
    return this.getItem(SETTING_KEYS.timeMaxLate);
  }

  static isAutoAddMaxLateEnabled(): boolean {
    return this.getItem(SETTING_KEYS.autoAddMaxLate) === "true";
  }

  static hasRefreshSchedulesFlag(): boolean {
    return this.getItem(SETTING_KEYS.refreshSchedules) != null;
  }

  static getTourColumnsSaved<T>(fallback: T): T {
    return this.getJSON(SETTING_KEYS.tourColumnsSaved, fallback);
  }

  static getHourGroupColumn(fallback: "start" | "registration_time" = "start"): "start" | "registration_time" {
    const v = this.getItem(SETTING_KEYS.hourGroupColumn);
    return v === "registration_time" || v === "start" ? v : fallback;
  }

  static getGridColumnState<T>(fallback: T): T {
    return this.getJSON(SETTING_KEYS.gridColumnState, fallback);
  }

  static getCurrencyRatesCache<T>(fallback: T): T {
    return this.getJSON(SETTING_KEYS.currencyRatesCache, fallback);
  }

  static getUserTableSize(userId: number | string, fallback = 1): number {
    const cache = this.ensureUserCache(userId);
    const v = cache?.prefs?.tableSize;
    if (v == null) return fallback;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  static setUserTableSize(userId: number | string, value: number): void {
    const uid = Number(userId);
    let cache = this.ensureUserCache(userId);
    if (!cache) {
      cache = { prefs: {}, playList: [], listIds: { skip: [], bustLate: [], hidden: [], removedBustLate: [] } };
      this.userCache.set(uid, cache);
    }
    cache.prefs.tableSize = String(value);
    this.backend.setUserPref(uid, "tableSize", String(value)).catch(() => {});
  }

  static getUserIsSimpleMode(userId: number | string, fallback = false): boolean {
    const cache = this.ensureUserCache(userId);
    if (!cache?.prefs?.isSimpleMode) return fallback;
    return cache.prefs.isSimpleMode === "true";
  }

  static setUserIsSimpleMode(userId: number | string, value: boolean): void {
    const uid = Number(userId);
    let cache = this.ensureUserCache(userId);
    if (!cache) {
      cache = { prefs: {}, playList: [], listIds: { skip: [], bustLate: [], hidden: [], removedBustLate: [] } };
      this.userCache.set(uid, cache);
    }
    cache.prefs.isSimpleMode = String(value);
    this.backend.setUserPref(uid, "isSimpleMode", String(value)).catch(() => {});
  }

  static getUserGrindSessionTimestamp(userId: number | string): number | null {
    const cache = this.ensureUserCache(userId);
    const v = cache?.prefs?.grindSession;
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }

  static setUserGrindSessionTimestamp(userId: number | string, timestamp: number): void {
    const uid = Number(userId);
    let cache = this.ensureUserCache(userId);
    if (!cache) {
      cache = { prefs: {}, playList: [], listIds: { skip: [], bustLate: [], hidden: [], removedBustLate: [] } };
      this.userCache.set(uid, cache);
    }
    cache.prefs.grindSession = String(timestamp);
    this.backend.setUserPref(uid, "grindSession", String(timestamp)).catch(() => {});
  }

  static clearUserSessionRuntimeData(userId: number | string): void {
    this.clearUserList(userId, "bustLate");
    this.clearUserList(userId, "skip");
    this.clearUserList(userId, "hidden");
    this.clearUserList(userId, "removedBustLate");
  }
}

const LEGACY_TO_SETTING: Record<string, string> = {
  "table-zoom": SETTING_KEYS.tableZoom,
  autoResizeTable: SETTING_KEYS.autoResizeTable,
  timeMaxLate: SETTING_KEYS.timeMaxLate,
  autoAddMaxLate: SETTING_KEYS.autoAddMaxLate,
  "refresh-schedules": SETTING_KEYS.refreshSchedules,
  "tourColumnsSaved-grind": SETTING_KEYS.tourColumnsSaved,
  hourGroupColumn: SETTING_KEYS.hourGroupColumn,
  grindColState: SETTING_KEYS.gridColumnState,
  currency_rates_cache: SETTING_KEYS.currencyRatesCache,
};
